import View from '@play-co/timestep-core/lib/ui/View';
import Tab from './Tab';

type Props = {
  selectedTabIndex?: number;
};

const skin = {
  tab: {
    height: 51,
    style: {
      margin: -40,
    },
  },
};

export type Alignment = 'left' | 'center';

export type TabOpts = {
  label: () => string;
  tracking: { feature: string; $subFeature: string };
};

export default class Tabs {
  private container: View;
  private props: Props = {};
  private tabs: Tab[] = [];

  constructor(opts: {
    alignment: Alignment;
    superview: View;
    rect: { x: number; y: number; width: number };
    tabs: TabOpts[];
    onTabSelected: (index: number) => void;
  }) {
    this.container = new View({
      superview: opts.superview,
      x: opts.rect.x,
      y: opts.rect.y - skin.tab.height,
      width: opts.rect.width,
      height: skin.tab.height,
    });

    const buttonsCount = opts.tabs.length;

    for (let index = 0; index < buttonsCount; ++index) {
      const tab = opts.tabs[index];
      // for centered view, we can only calculate position when all buttons are rendered
      const offsetX =
        opts.alignment === 'left'
          ? this.getOffsetX(index, skin.tab.style.margin)
          : 0;

      this.tabs.push(
        new Tab({
          superview: this.container,
          markup: {
            offsetX,
            buttonsCount,
            style: {
              ...skin.tab.style,
            },
          },

          selected: !index,

          ...tab,

          onSelect: () => {
            if (!this.isExternallyControlled()) {
              this.onTabSelected(index);
            }

            opts.onTabSelected(index);
          },
        }),
      );
    }

    if (opts.alignment === 'center') {
      this.repositionTabs();
    }
  }

  private getOffsetX(index: number, margin: number) {
    const previousTab = this.tabs[index - 1];
    const previousTabStyle = previousTab && previousTab.getView().style;
    return previousTabStyle
      ? previousTabStyle.x + previousTabStyle.width + margin
      : 0;
  }

  private repositionTabs() {
    const parentWidth = this.container.style.width;
    const commonButtonWidth = this.tabs.reduce((result, tab) => {
      return result + tab.getView().style.width;
    }, 0);
    const calculatedMargin =
      (parentWidth - commonButtonWidth) / (this.tabs.length - 1);

    for (let index = 0; index < this.tabs.length; ++index) {
      const x = this.getOffsetX(index, calculatedMargin);
      this.tabs[index].updateOpts({ x });
    }
  }

  destroy() {
    this.container.removeFromSuperview();
  }

  setProps(props: Props) {
    this.props = props;

    if (this.isExternallyControlled()) {
      this.onTabSelected(props.selectedTabIndex);
    }
  }

  private onTabSelected(selectedTabIndex: number) {
    this.tabs.forEach((tab, i) => {
      tab.setProps({ selected: i === selectedTabIndex });
    });
  }

  private isExternallyControlled() {
    return 'selectedTabIndex' in this.props;
  }
}
